import cn from 'classnames';
import { type MemoExoticComponent, memo } from 'react';

import type { AvatarShape } from '@didaskill/types/web';
import { ComponentSwitch } from '@didaskill/ui/utils';
import { getInitials } from '@didaskill/utils/misc';

import DefaultDidaskillAvatar from '../../../../assets/images/default_didaskill_avatar.png';
import type { TYPOGRAPHY_VARIANTS } from '../../../config/typography/constants';
import { useUserThemeColor } from '../../../utils/hooks/useUserThemeColor';
import { Typography } from '../../general/typography/Typography';
import * as styles from './Avatar.styles';
import { AVATAR_CONNECTION_STATUS, AVATAR_SHAPES, AVATAR_SIZES } from './constants';

const TYPOGRAPHY_SIZE_MAPPING: Record<AVATAR_SIZES, TYPOGRAPHY_VARIANTS> = {
  [AVATAR_SIZES.SMALL]: Typography.VARIANTS.BODY_SMALL_BOLD,
  [AVATAR_SIZES.MEDIUM]: Typography.VARIANTS.BODY_MEDIUM_BOLD,
  [AVATAR_SIZES.LARGE]: Typography.VARIANTS.BODY_LARGE_BOLD,
  [AVATAR_SIZES.OVERSIZED]: Typography.VARIANTS.HEADING_2,
};

export type AvatarProps = {
  /** User to whom the avatar belongs. */
  user: AvatarShape;
  /** Avatar size. */
  size?: AVATAR_SIZES;
  /** With a box-shadow effect. */
  isShadowed?: boolean;
  /** Style applied to the component. */
  className?: string;
  /** Shape of the avatar. */
  shape?: AVATAR_SHAPES;
  /** Use default Didaskill picture */
  useDefaultAvatar?: boolean;
  /** Optional connection status useful to display dot with right color */
  connectionStatus?: AVATAR_CONNECTION_STATUS;
};

export const AvatarBase = ({
  user,
  size = AVATAR_SIZES.MEDIUM,
  isShadowed = false,
  className = null,
  shape = AVATAR_SHAPES.CIRCLE,
  useDefaultAvatar = false,
  connectionStatus,
}: AvatarProps) => {
  const themeColor = useUserThemeColor(user.fullName);

  const initials = getInitials(...user.fullName.split(' '));

  const firstInitial = initials[0];
  const lastInitial = initials.length > 1 ? initials[initials.length - 1] : '';

  const initialsToDisplay = `${firstInitial}${lastInitial}`;

  const pictureHref = user.picture?.href;

  return (
    <span
      css={styles.containerStyle(themeColor)}
      className={cn(className, size, shape, { isShadowed })}
    >
      <ComponentSwitch value={true}>
        <ComponentSwitch.Item value={useDefaultAvatar}>
          <img src={DefaultDidaskillAvatar} alt="default avatar" />
        </ComponentSwitch.Item>

        <ComponentSwitch.Item value={!!pictureHref}>
          {isShadowed && (
            /* This is the shadow overlay behind the picture. */
            /* Container. */
            <div css={styles.profilePictureShadowContainerStyle} className={cn(shape)}>
              {/* Picture + blur. */}
              <div css={styles.profilePictureShadowStyle(pictureHref)} />

              {/* Transparent white overlay. */}
              <div css={styles.profilePictureShadowOverlayStyle} />
            </div>
          )}

          {/* This is the picture. */}
          <div css={styles.profilePictureStyle(pictureHref)} className={cn(shape)} />
        </ComponentSwitch.Item>

        <ComponentSwitch.Item value={!pictureHref && !useDefaultAvatar}>
          <span css={styles.defaultAvatarStyle(themeColor)} className={cn(size, shape)}>
            <Typography variant={TYPOGRAPHY_SIZE_MAPPING[size]} element="span">
              {initialsToDisplay}
            </Typography>
          </span>
        </ComponentSwitch.Item>
      </ComponentSwitch>

      {connectionStatus && <span css={styles.dotStatus} className={cn(connectionStatus, size)} />}
    </span>
  );
};

AvatarBase.defaultName = 'Avatar';

export const Avatar: MemoExoticComponent<typeof AvatarBase> & {
  SIZES?: typeof AVATAR_SIZES;
  SHAPES?: typeof AVATAR_SHAPES;
  STATUS?: typeof AVATAR_CONNECTION_STATUS;
} = memo(AvatarBase);

Avatar.SIZES = AVATAR_SIZES;
Avatar.SHAPES = AVATAR_SHAPES;
Avatar.STATUS = AVATAR_CONNECTION_STATUS;
